import React, { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

interface RichTextEditorProps {
    value: string;
    onChange: (val: string) => void;
    placeholder?: string;
    height?: string;
    className?: string;
    name?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, placeholder, height, className }) => {
    const [viewSource, setViewSource] = useState(false);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [3],
                },
            }),
        ],
        content: value,
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
        editorProps: {
            attributes: {
                class: 'prose prose-sm focus:outline-none max-w-none content-prose',
            },
            transformPastedHTML(html) {
                // Strip inline formatting tags on paste — keep block structure only
                return html
                    .replace(/<\/?(em|i|strong|b|u|s|strike|sub|sup|mark|span|a|font)[^>]*>/gi, '')
                    .replace(/\s*style="[^"]*"/gi, '')
                    .replace(/\s*class="[^"]*"/gi, '');
            },
        },
    });

    // Sync content if value changes externally (and it's different from editor content)
    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value);
        }
    }, [value, editor]);

    // Use height prop for min-height, but allow it to grow (auto-height)
    const minHeight = height || '150px';

    const toggleSource = () => {
        if (viewSource) {
            editor?.commands.setContent(value);
        }
        setViewSource(!viewSource);
    };

    if (!editor) {
        return null;
    }

    // Editor wrapper is the scroll container so sticky toolbar works
    // regardless of parent overflow-hidden panels in the admin layout
    const wrapperMaxHeight = height ? `max(${height}, 500px)` : '600px';

    return (
        <div className={`rich-text-editor-wrapper ${className || ''} flex flex-col border border-gray-300 rounded-sm bg-white overflow-y-auto`}
             style={{ maxHeight: wrapperMaxHeight }}>
            <style>{`
                .ProseMirror {
                     min-height: ${minHeight};
                     padding: 0.75rem;
                     outline: none;
                     /* Match public site: no override — content-prose handles spacing */
                }
                /* paragraph spacing = 1rem gap (matches content-prose on public site) */
                .ProseMirror p { margin-block: 1rem; }
                .ProseMirror p:first-child { margin-top: 0; }
                .ProseMirror p:last-child { margin-bottom: 0; }
                /* br inside paragraph = just line-height, no extra gap (WYSIWYG) */
                .ProseMirror br { display: block; content: ''; }
                .ProseMirror h3 { font-size: 1.15rem; font-weight: bold; margin-block: 1.25em 0.5em; }
                .ProseMirror ul { list-style-type: disc; padding-left: 1.25em; margin-block: 1rem; }
                .ProseMirror ol { list-style-type: decimal; padding-left: 1.25em; margin-block: 1rem; }

                .tiptap-toolbar button {
                    padding: 2px 6px;
                    border-radius: 2px;
                    font-size: 11px;
                    background: #f8f8f8;
                    border: 1px solid #ddd;
                    transition: all 0.1s;
                    cursor: pointer;
                }
                .tiptap-toolbar button:hover { background: #eee; }
                .tiptap-toolbar button.is-active { background: #666; color: white; border-color: #444; }

                /* sticky toolbar — works because wrapper is the scroll container */
                .tiptap-sticky-bar {
                    position: sticky;
                    top: 0;
                    z-index: 20;
                    background: #f0f0f0;
                    border-bottom: 1px solid #d1d5db;
                    flex-shrink: 0;
                }
                /* hint bar at bottom also sticks */
                .tiptap-hint-bar {
                    position: sticky;
                    bottom: 0;
                    background: #f9f9f9;
                    flex-shrink: 0;
                }
            `}</style>

            <div className="tiptap-sticky-bar flex items-center justify-between px-2 py-1">
                <div className="flex gap-1.5 tiptap-toolbar">
                    {!viewSource && (
                        <>
                            <button
                                type="button"
                                onClick={() => editor.chain().focus().toggleBold().run()}
                                className={editor.isActive('bold') ? 'is-active' : ''}
                                title="Bold"
                            >
                                B
                            </button>
                            <button
                                type="button"
                                onClick={() => editor.chain().focus().toggleItalic().run()}
                                className={editor.isActive('italic') ? 'is-active' : ''}
                                title="Italic"
                            >
                                I
                            </button>
                            <button
                                type="button"
                                onClick={() => editor.chain().focus().toggleUnderline().run()}
                                className={editor.isActive('underline') ? 'is-active' : ''}
                                title="Underline"
                            >
                                U
                            </button>
                            <div className="w-px h-3 bg-gray-400 mx-0.5 self-center" />
                            <button
                                type="button"
                                onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                                className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
                                title="Heading 3"
                            >
                                H3
                            </button>
                            <div className="w-px h-3 bg-gray-400 mx-0.5 self-center" />
                            <button
                                type="button"
                                onClick={() => editor.chain().focus().toggleBulletList().run()}
                                className={editor.isActive('bulletList') ? 'is-active' : ''}
                                title="Bullet List"
                            >
                                List
                            </button>
                            <button
                                type="button"
                                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                                className={editor.isActive('orderedList') ? 'is-active' : ''}
                                title="Numbered List"
                            >
                                Num
                            </button>
                            <div className="w-px h-3 bg-gray-400 mx-0.5 self-center" />
                            <button
                                type="button"
                                onClick={() => editor.chain().focus().unsetAllMarks().run()}
                                title="Formatierung entfernen (Strg+Klick nach Markierung)"
                                className="text-red-400 hover:text-red-600"
                            >
                                Tx
                            </button>
                        </>
                    )}
                </div>

                <button
                    type="button"
                    onClick={toggleSource}
                    className="text-[10px] text-gray-600 hover:text-black font-bold uppercase tracking-tight px-1"
                >
                    {viewSource ? '[ Editor ]' : '[ HTML ]'}
                </button>
            </div>

            {viewSource ? (
                <textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full p-3 font-mono text-xs bg-gray-50 focus:outline-none resize-y"
                    style={{ minHeight: minHeight }}
                />
            ) : (
                <div className="bg-white">
                    <EditorContent editor={editor} />
                </div>
            )}
            {!viewSource && (
                <div className="tiptap-hint-bar px-3 py-1 border-t border-gray-200 text-[10px] text-gray-600 flex gap-4">
                    <span><kbd className="font-mono bg-gray-300 px-1 rounded text-gray-700">Enter</kbd> = neuer Absatz (großer Abstand)</span>
                    <span><kbd className="font-mono bg-gray-300 px-1 rounded text-gray-700">Shift+Enter</kbd> = neue Zeile (kleiner Abstand)</span>
                </div>
            )}
        </div>
    );
};

export default RichTextEditor;
